// eslint-disable-next-line import/no-extraneous-dependencies
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // this is for error and session storage
  error: null,
  sessions: [], // Changed from `session` to array `sessions`
  incomingCalls: [], // tracing multiple incoming calls
  // this is for mute and hold

  // this is for audio control
  audioRef: null,
  audioVolume: 1,

  // this is for register and tracking session status
  isRegistered: false,

  /* Transfer call modal state */

  isCallControlModal: false,

  // ✅ NEW
  monitorSessions: [], // Store sessions created by barge/listen/whisper

  // Conference calling state
  conferences: {}, // { conferenceGroupId: { groupId, sessions: [], createdAt, audioData: {} } }
};

const callSlice = createSlice({
  name: 'call',
  initialState,
  reducers: {
    setError: (state, { payload }) => {
      state.error = payload;
    },
    /* ---------------------------------------------------------------------- */
    // 🔥 Add/Remove/Update sessions
    addSession: (state, { payload }) => {
      state.sessions.push(payload);
    },
    removeSession: (state, { payload }) => {
      state.sessions = state.sessions.filter((s) => s.id !== payload);
    },
    updateSession: (state, { payload }) => {
      const { id, ...updates } = payload;
      const index = state.sessions.findIndex((s) => s.id === id);
      if (index !== -1) {
        state.sessions[index] = {
          ...state.sessions[index],
          ...updates,
        };
      }
    },
    clearSessions: (state) => {
      state.sessions = [];
    },
    /* ------------------------------------------------------------------------------------- */

    /* -----------------------------Managing Incoming Calls----------------------------------------- */
    addIncomingCall: (state, action) => {
      const incoming = action.payload; // normalized session, which has .id property
      const exists = state.incomingCalls.some((call) => call.id === incoming.id);
      if (!exists) state.incomingCalls.push(incoming);
    },

    removeIncomingCall: (state, action) => {
      // action.payload is session id (string)
      state.incomingCalls = state.incomingCalls.filter((call) => call.id !== action.payload);
    },

    acceptCall: (state, action) => {
      // action.payload is normalized session object with .id
      state.currentSession = action.payload;
      state.incomingCalls = state.incomingCalls.filter((call) => call.id !== action.payload.id);
    },

    rejectCall: (state, action) => {
      const sessionId = action.payload;
      state.incomingCalls = state.incomingCalls.filter((call) => call.id !== sessionId);
    },

    setIncomingCallModal: (state, { payload }) => {
      state.isCallControlModal = payload;
    },

    setIncomingCallAccepted: (state, { payload }) => {
      state.incomingCallAccepted = payload;
    },
    setRegisterUA: (state, { payload }) => {
      state.isRegistered = payload;
    },
    setTransferCallModalState: (state, { payload }) => {
      state.isCallControlModal = payload;
    },

    /* Addend Transfer */
    markAttendedTransfer(state, action) {
      const { sourceId, targetSession } = action.payload;

      const sourceIndex = state.sessions.findIndex((s) => s.id === sourceId);
      if (sourceIndex !== -1) {
        state.sessions[sourceIndex].isBeingTransferred = true;
        state.sessions[sourceIndex].transferTargetId = targetSession.id;
      }

      // Ensure reverse link is set
      targetSession.isTransferTarget = true;
      targetSession.transferSourceId = sourceId;

      state.sessions.push(targetSession);
    },
    resetTransferFlags(state, action) {
      state.sessions = state.sessions.map((s) => ({
        ...s,
        isBeingTransferred: false,
        transferTargetId: null,
        isTransferTarget: false,
        transferSourceId: null,
      }));
    },

    /* Monitoring */
    addMonitorSession: (state, { payload }) => {
      const exists = state.monitorSessions.some((s) => s.id === payload.id);
      if (!exists) {
        state.monitorSessions.push(payload);
      }
    },
    removeMonitorSession: (state, { payload }) => {
      state.monitorSessions = state.monitorSessions.filter((s) => s.id !== payload);
    },
    updateMonitorSession: (state, { payload }) => {
      const { id, ...updates } = payload;
      const index = state.monitorSessions.findIndex((s) => s.id === id);
      if (index !== -1) {
        state.monitorSessions[index] = {
          ...state.monitorSessions[index],
          ...updates,
        };
      }
    },
    clearMonitorSessions: (state) => {
      state.monitorSessions = [];
    },

    /* ----------------------------------- */

    /* Conference calling actions */
    createConference: (state, { payload }) => {
      const { groupId, sessionIds, audioData } = payload;
      state.conferences[groupId] = {
        groupId,
        sessions: sessionIds,
        createdAt: new Date().toISOString(),
        audioData: audioData || {},
      };

      // Mark sessions as part of conference
      sessionIds.forEach((sessionId) => {
        const session = state.sessions.find((s) => s.id === sessionId);
        if (session) {
          session.isConference = true;
          session.conferenceGroupId = groupId;
        }
      });
    },

    updateConferenceAudio: (state, { payload }) => {
      const { groupId, audioData } = payload;
      if (state.conferences[groupId]) {
        state.conferences[groupId].audioData = {
          ...state.conferences[groupId].audioData,
          ...audioData,
        };
      }
    },

    endConference: (state, { payload }) => {
      const groupId = payload;
      const conference = state.conferences[groupId];

      if (conference) {
        // Unmark sessions
        conference.sessions.forEach((sessionId) => {
          const session = state.sessions.find((s) => s.id === sessionId);
          if (session) {
            session.isConference = false;
            session.conferenceGroupId = null;
          }
        });

        // Remove conference
        delete state.conferences[groupId];
      }
    },

    /* ----------------------------------------------------------------------------- */
    // New reducer to reset state except `ua`
    resetState: (state) => ({
      ...initialState,
      sessionStatus: state.sessionStatus,
      isRegistered: state.isRegistered,
    }),
  },
});

export const {
  setError,
  addSession,
  removeSession,
  updateSession,
  clearSessions,
  addIncomingCall,
  removeIncomingCall,
  acceptCall,
  rejectCall,
  setIncomingCallModal,
  setIncomingCallAccepted,
  setRegisterUA,
  setTransferCallModalState,
  resetState,
  markAttendedTransfer,
  resetTransferFlags,
  addMonitorSession,
  removeMonitorSession,
  updateMonitorSession,
  clearMonitorSessions,
  createConference,
  updateConferenceAudio,
  endConference,
} = callSlice.actions;

/* MUte unmute hold and unhold */
export const selectIsGlobalMuted = (state) =>
  state.call.sessions.every((session) => session.isMuted);

/* Incoming call */
export const incomingCallerModal = (state) => state.call.incomingCallModal;
export const selectIncomingCallAccepted = (state) => state.call.incomingCallAccepted;
export const selectCallerNumber = (state) => state.call.callerNumber;

/* Jssip ua register and tracking session */
export const selectSessions = (state) => state.call.sessions || [];
export const selectRegisteredUA = (state) => state.call.isRegistered;

export const selectTransferCallModalValue = (state) => state.call.isCallControlModal;
export const selectIncomingCalls = (state) => state.call.incomingCalls;
export const getIncomingSessionById = (state, sessionId) =>
  state.call.incomingSessions?.find((session) => session.id === sessionId);
export const selectMonitorSessions = (state) => state.call.monitorSessions || [];

/* Conference selectors */
export const selectConferences = (state) => state.call.conferences || {};
export const selectConferenceByGroupId = (state, groupId) => state.call.conferences[groupId];
export const selectIsSessionInConference = (state, sessionId) => {
  const session = state.call.sessions.find((s) => s.id === sessionId);
  return session && session.isConference === true;
};

export default callSlice.reducer;
